import React, { useEffect, useMemo, useState } from "react";
import {
  faAngleDown,
  faAngleUp,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import ComponentLoader from "./ComponentLoader";
import { convertFileToImageData } from "../helpers/GenericHelpers";
import TableComponent from "../components/widgets/Table/TableComponent";
import { readString } from "react-papaparse";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface IFileViewerProps {
  file: any;
  loader?: boolean;
  showPreview?: boolean;
}

const FileViewer = (props: IFileViewerProps) => {
  const { file } = props;

  // states
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageScale, setPageScale] = useState(1);
  const [data, setData] = useState<any>(null);
  const [loader, setLoader] = useState(false);
  const [csvExtract, setCsvExtract] = useState<any>([]);

  // helpers
  const onDocumentLoadSuccess = ({ numPages: nextNumPages }: any) => {
    setNumPages(nextNumPages);
  };

  function handleZoomIn() {
    if (pageScale < 3) {
      setPageScale(pageScale + 0.2);
    }
  }

  function handleZoomOut() {
    if (pageScale > 0.3) {
      setPageScale(pageScale - 0.2);
    }
  }

  function handleNext() {
    if (numPages && pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  }
  function handlePrevious() {
    if (pageNumber > 0) {
      setPageNumber(pageNumber - 1);
    }
  }

  const fetchData = (start: number, size: number, tableData: any) => {
    return {
      data: tableData.slice(start, start + size),
      meta: {
        totalRowCount: tableData.length,
      },
    };
  };

  const columns = useMemo(
    () => (csvExtract?.length > 0 ? csvExtract?.[0]?.map((each: any) => ({
      accessorKey: each
    })) : []),
    [csvExtract]
  );

  const tableData = useMemo(
    () => (
      csvExtract?.length > 1 ? 
        csvExtract?.slice(1)?.map(
          (init: any) => {
            const values = {}
            init?.forEach((each: any, index: number) => Object.assign(values, {
              [columns?.[index]?.accessorKey]: each
            }))
            return values;
          }
        )
        : []
      ),
    [csvExtract]
  );

  useEffect(() => {
    if (file?.type?.includes("csv")) {
      setLoader(true);
      readString(file, {
        worker: true,
        complete: (results) => {
          setCsvExtract(results?.data || []);
          setLoader(false);
        },
      });
    } else if (
      file?.type?.includes("text") ||
      file?.type?.includes("jpg") ||
      file?.type?.includes("jpeg") ||
      file?.type?.includes("png")
    ) {
      convertFileToImageData(file, setData, setLoader);
    }
  }, []);

  const displayCSVFile = useMemo(() => columns?.length > 0 && tableData?.length > 0 ? 
    <TableComponent
      columns={columns}
      tableData={tableData}
      infiniteScroll={{
        display: true,
        fetchSize: 25,
        handlefetch: fetchData,
      }}
      enableSort={true}
      enableFilter={true}
      enableGlobalFilter={true}
    /> : (
    <div className="not-found">Something went wrong</div>
  ), [columns, tableData])
  // display functions
  const displayFile = () => {
    // console.log(file);
    if (file?.type?.includes("pdf")) {
      return (
        <div className="file-viewer">
          {props?.showPreview ? 
          <div  className="page-preview">
          { Array(numPages).fill(undefined).map((_, index) => (
            <div key={index}>
               <Document
                className={`file-view-container ${index+1 === pageNumber ? 'selected' :''}`}
                file={
                  file?.value
                    ? `data:application/${file?.fileType};base64,${file?.value}`
                    : file
                }
                onLoadSuccess={onDocumentLoadSuccess}
              >
                <Page pageNumber={index+1} />
              </Document>
            </div>
          ))}
          </div> : ''}
        <Document
          className="file-view-container"
          file={
            file?.value
              ? `data:application/${file?.fileType};base64,${file?.value}`
              : file
          }
          onLoadSuccess={onDocumentLoadSuccess}
        >
          <Page pageNumber={pageNumber} scale={pageScale} />
          <div className="doc-options">
            <div className="page-number">
              {pageNumber === 1 ? (
                <></>
              ) : (
                <FontAwesomeIcon
                  className="page-options page-up"
                  icon={faAngleUp}
                  onClick={handlePrevious}
                />
              )}
              {pageNumber === numPages ? (
                <></>
              ) : (
                <FontAwesomeIcon
                  className="page-options page-down"
                  icon={faAngleDown}
                  onClick={handleNext}
                />
              )}
              <div className="page-text">
                Showing {pageNumber} of {numPages}
              </div>
            </div>
            <div className="zoom-container">
              <div className="zoom-value">{pageScale * 100} %</div>
              {pageScale >= 3 ? (
                <></>
              ) : (
                <FontAwesomeIcon
                  className="zoom-options page-up"
                  icon={faMagnifyingGlassPlus}
                  onClick={handleZoomIn}
                />
              )}
              {pageScale <= 0.3 ? (
                <></>
              ) : (
                <FontAwesomeIcon
                  className="zoom-options page-up"
                  icon={faMagnifyingGlassMinus}
                  onClick={handleZoomOut}
                />
              )}
            </div>
          </div>
        </Document>
        </div>
      );
    } else if (
      file?.type?.includes("jpg") ||
      file?.type?.includes("jpeg") ||
      file?.type?.includes("png")
    ) {
      let objectUrl;
      if (file?.value) {
        objectUrl = `data:application/${file?.fileType};base64,${file?.value}`;
      } else {
        objectUrl = data;
      }
      return <img className="file-image" src={objectUrl} alt="file" />;
    } else if (file?.type?.includes("csv")) {
      return displayCSVFile;
    } else if (file?.type?.includes("text")) {
      return <iframe src={data} className="text-frame" />;
    } else
      return <div className="not-found">No preview for this file format</div>;
  };

  return <div className="file-viewer-main">{loader ? <ComponentLoader /> : displayFile()}</div>;
};

export default FileViewer;
